import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Award, BarChart3, Building2, ChevronRight, Copy, LayoutGrid, LogIn, PlusCircle, Share2, User, UserCircle, Users } from "lucide-react";
import { api, copyText, shareText } from "@/lib/api";
import { isManagerRole, useAuth } from "@/lib/auth";
import { usePendingApprovals } from "@/lib/pending";
import { useToast } from "@/lib/toast";
import { Button, Card, Input, Overline } from "@/components/app/Primitives";
import { StackPage } from "@/components/app/Layout";

const TABS = [{ key: "mine", label: "My spaces", icon: LayoutGrid }, { key: "join", label: "Join", icon: LogIn }, { key: "create", label: "Create", icon: PlusCircle }];
const cap = (r) => (r ? r.charAt(0).toUpperCase() + r.slice(1) : "");

export default function Workspaces() {
  const { workspaces, activeWorkspace: active, setActiveWorkspace, createWorkspace, refreshWorkspaces } = useAuth();
  const { toApprove, toAccept, approve, reject, accept, decline, refresh: refreshPending } = usePendingApprovals();
  const toast = useToast();
  const navigate = useNavigate();
  const [tab, setTab] = useState("mine");
  const [name, setName] = useState("");
  const [type, setType] = useState("company");
  const [busy, setBusy] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);
  const totalPending = toApprove.length + toAccept.length;
  const isActiveManager = active ? isManagerRole(active.role) : false;
  const others = workspaces.filter((w) => w.id !== active?.id);

  const pick = async (id) => {
    if (id === active?.id) return;
    await setActiveWorkspace(id);
    toast.show("Workspace switched", "success");
    navigate("/", { replace: true });
  };
  const create = async () => {
    if (!name.trim()) return toast.show("Workspace name required", "error");
    setBusy(true);
    try { await createWorkspace(name.trim(), type); toast.show("Workspace created", "success"); setName(""); navigate("/", { replace: true }); }
    catch (e) { toast.show(e instanceof Error ? e.message : "Failed", "error"); } finally { setBusy(false); }
  };
  const join = async () => {
    if (!joinCode.trim()) return toast.show("Enter a workspace code", "error");
    setJoining(true);
    try {
      const r = await api("/workspaces/join-by-code", { method: "POST", body: { code: joinCode.trim().toUpperCase() } });
      setJoinCode("");
      if (r.status === "active") { toast.show("Already a member — activated.", "success"); await refreshWorkspaces(); }
      else toast.show("Request sent. Waiting for manager approval.", "info");
      await refreshPending();
    } catch (e) { toast.show(e instanceof Error ? e.message : "Join failed", "error"); } finally { setJoining(false); }
  };
  const copyCode = async (code) => { if (!code) return; await copyText(code); toast.show(`Copied ${code}`, "success"); };
  const shareCode = async (w) => {
    if (!w.code) return;
    const r = await shareText({ message: `Join "${w.name}" on Assignoo. Enter this code in Workspaces → Join: ${w.code}`, title: `Join ${w.name}` });
    if (r === "copied") toast.show("Invite message copied to clipboard", "success");
  };

  const smallBtn = "rounded-lg px-3 py-2 text-xs font-bold";
  const pendingCard = totalPending === 0 ? null : (
    <Card testID="workspaces-pending">
      <div className="flex items-center gap-2"><span className="h-2 w-2 rounded-full bg-a-warning" /><p className="flex-1 font-extrabold text-a-text">Pending actions</p><span className="text-xs text-a-muted">{totalPending}</span></div>
      {toAccept.length > 0 ? (
        <div className="mt-2.5 space-y-2">
          <p className="text-[11px] font-extrabold tracking-[1.2px] text-a-muted">INVITES FOR YOU</p>
          {toAccept.map((m) => (
            <div key={m.id} className="flex items-center gap-2 rounded-xl border border-a-border p-2.5">
              <div className="min-w-0 flex-1"><p className="truncate font-bold text-a-text">{m.workspace_name}</p><p className="text-xs text-a-muted">Role: {m.role}</p></div>
              <button onClick={() => accept(m.id)} className={`${smallBtn} bg-a-primary text-white`} data-testid={`invite-accept-${m.id}`}>Accept</button>
              <button onClick={() => decline(m.id)} className={`${smallBtn} border border-a-border bg-a-surface text-a-danger`} data-testid={`invite-decline-${m.id}`}>Decline</button>
            </div>
          ))}
        </div>
      ) : null}
      {toApprove.length > 0 ? (
        <div className="mt-3 space-y-2">
          <p className="text-[11px] font-extrabold tracking-[1.2px] text-a-muted">REQUESTS TO APPROVE</p>
          {toApprove.map((m) => (
            <div key={m.id} className="flex items-center gap-2 rounded-xl border border-a-border p-2.5">
              <div className="min-w-0 flex-1"><p className="truncate font-bold text-a-text">{m.full_name}</p><p className="truncate text-xs text-a-muted">{m.email} · {m.workspace_name}</p></div>
              <button onClick={() => approve(m.id, "employee")} className={`${smallBtn} bg-a-success text-white`} data-testid={`request-approve-${m.id}`}>Approve</button>
              <button onClick={() => reject(m.id)} className={`${smallBtn} border border-a-border bg-a-surface text-a-danger`} data-testid={`request-reject-${m.id}`}>Reject</button>
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );

  const hero = (icon, title, sub) => (
    <div className="flex flex-col items-center pb-2 pt-1 text-center">
      <span className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-a-dim text-a-primary">{icon}</span>
      <p className="mt-2 text-[17px] font-extrabold text-a-text">{title}</p>
      <p className="mt-1 text-[13px] text-a-muted">{sub}</p>
    </div>
  );

  return (
    <StackPage title="Workspaces" subtitle="Switch, invite teammates or join a new one" testID="workspaces-page" contentClassName="">
      <div className="flex gap-2 px-4 pt-3">
        {TABS.map((t) => {
          const on = tab === t.key;
          return (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`ws-tab-${t.key}`}
              className={`flex flex-1 items-center justify-center gap-1.5 rounded-xl border py-2.5 text-[13px] font-bold transition-colors ${on ? "border-a-primary bg-a-primary text-white" : "border-a-border bg-a-surface text-a-text hover:border-a-primary/50"}`}>
              <t.icon size={14} className={on ? "" : "text-a-muted"} /> {t.label}
              {t.key === "mine" && totalPending > 0 ? <span className={`flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[10px] font-extrabold ${on ? "bg-white text-a-primary" : "bg-a-danger text-white"}`}>{totalPending > 9 ? "9+" : totalPending}</span> : null}
            </button>
          );
        })}
      </div>
      <div className="space-y-3.5 p-4">
        {tab === "mine" ? (
          <>
            {pendingCard}
            {active ? (
              <Card className="!border-[1.5px] !border-a-primary" testID="active-workspace-card">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-[14px] bg-a-primary text-white">{active.type === "company" ? <Building2 size={22} /> : <User size={22} />}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-[11px] font-extrabold uppercase tracking-[1.4px] text-a-muted">Active workspace</p>
                    <p className="truncate text-lg font-extrabold text-a-text">{active.name}</p>
                    <div className="mt-1 flex items-center gap-1.5">
                      <span className="flex items-center gap-1 rounded-full bg-a-dim px-2 py-0.5 text-xs font-bold text-a-primary">{isActiveManager ? <Award size={12} /> : <UserCircle size={12} />}{cap(active.role)}</span>
                      <span className="text-xs text-a-muted">· {active.type}</span>
                    </div>
                  </div>
                </div>
                {active.code && isActiveManager ? (
                  <div className="mt-3 flex items-center gap-3 rounded-xl border border-a-border bg-a-bg p-3">
                    <div className="flex-1">
                      <p className="text-[10px] font-extrabold tracking-[1.4px] text-a-muted">WORKSPACE CODE</p>
                      <p className="text-[22px] font-extrabold tracking-[3px] text-a-text" data-testid="workspace-code">{active.code}</p>
                      <p className="mt-0.5 text-[11px] text-a-muted">Share this with teammates so they can request to join.</p>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <button onClick={() => shareCode(active)} data-testid="workspace-share-code" className="flex items-center gap-1 rounded-lg bg-a-primary px-2.5 py-1.5 text-xs font-bold text-white"><Share2 size={14} />Share</button>
                      <button onClick={() => copyCode(active.code)} data-testid="workspace-copy-code" className="flex items-center gap-1 rounded-lg border border-a-border bg-a-surface px-2.5 py-1.5 text-xs font-bold text-a-text"><Copy size={14} />Copy</button>
                    </div>
                  </div>
                ) : null}
                <div className="mt-3 flex gap-2">
                  <button onClick={() => navigate("/team")} data-testid="ws-open-team" className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-a-border bg-a-surface py-2.5 font-bold text-a-text hover:border-a-primary/50"><Users size={16} />Team</button>
                  <button onClick={() => navigate("/reports")} data-testid="ws-open-reports" className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-a-border bg-a-surface py-2.5 font-bold text-a-text hover:border-a-primary/50"><BarChart3 size={16} />Reports</button>
                  <button onClick={() => navigate("/", { replace: true })} data-testid="ws-open-dashboard" className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-a-primary py-2.5 font-bold text-white hover:brightness-110"><LayoutGrid size={16} />Open</button>
                </div>
              </Card>
            ) : (
              <Card><p className="text-a-muted">You don&apos;t have a workspace yet. Create one or join with a code.</p></Card>
            )}
            {others.length > 0 ? (
              <>
                <Overline muted className="mt-1">Other workspaces</Overline>
                {others.map((w) => (
                  <Card key={w.id} onClick={() => pick(w.id)} testID={`workspace-${w.id}`}>
                    <div className="flex items-center gap-3">
                      <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-a-dim text-a-primary">{w.type === "company" ? <Building2 size={18} /> : <User size={18} />}</span>
                      <div className="min-w-0 flex-1"><p className="truncate text-[15px] font-extrabold text-a-text">{w.name}</p><p className="text-xs text-a-muted">{cap(w.role)} · {w.type}{isManagerRole(w.role) && w.code ? ` · ${w.code}` : ""}</p></div>
                      <ChevronRight size={20} className="text-a-muted" />
                    </div>
                  </Card>
                ))}
              </>
            ) : null}
          </>
        ) : null}
        {tab === "join" ? (
          <>
            {pendingCard}
            <Card>
              {hero(<LogIn size={22} />, "Join a workspace", "Enter the code your manager shared. Your request will be pending until approved.")}
              <div className="mt-1 flex gap-2">
                <Input testID="ws-join-code" placeholder="ABCD1234" value={joinCode} onChangeText={(t) => setJoinCode(t.toUpperCase())} maxLength={12} onSubmitEditing={join} />
                <Button testID="ws-join-submit" label={joining ? "Joining…" : "Join"} onPress={join} loading={joining} fullWidth={false} className="h-12" />
              </div>
            </Card>
          </>
        ) : null}
        {tab === "create" ? (
          <Card>
            {hero(<PlusCircle size={22} />, "Create a workspace", <>Use <b className="text-a-text">Company</b> to invite teammates. <b className="text-a-text">Personal</b> is a solo space just for you.</>)}
            <div className="mt-2 space-y-3">
              <div className="flex gap-2.5">
                {["company", "personal"].map((t) => {
                  const on = type === t;
                  const Icon = t === "personal" ? UserCircle : Building2;
                  return (
                    <button key={t} onClick={() => setType(t)} data-testid={`ws-type-${t}`} className={`flex flex-1 items-center justify-center gap-2 rounded-xl border p-3 font-bold ${on ? "border-a-primary bg-a-dim text-a-primary" : "border-a-border bg-a-surface text-a-text"}`}>
                      <Icon size={18} className={on ? "" : "text-a-muted"} />{t === "personal" ? "Personal" : "Company"}
                    </button>
                  );
                })}
              </div>
              <Input testID="ws-create-name" label="Workspace name" placeholder={type === "company" ? "e.g. Acme Corp" : "e.g. Personal"} value={name} onChangeText={setName} onSubmitEditing={create} />
              <Button testID="ws-create-submit" label="Create workspace" onPress={create} loading={busy} />
            </div>
          </Card>
        ) : null}
      </div>
    </StackPage>
  );
}
